// features/workstations/dashboard-sections/daily-read.js
// ═══════════════════════════════════════════════════════════
// DAILY READ — the Sonnet-generated morning brief, sits at
// the top of the Workstation Dashboard. Per the Force Tracker
// spec — connects today's 7-force snapshot to the user's
// criteria.
//
// Output mirrors Signal Read structure:
//   • Market Direction (steel)    — broad institutional read
//   • Your Direction (gold)        — what YOUR criteria do
//   • The Gap (purple)             — where edge lives
//   • Watch List (teal)            — specific conditions today
//   • What to Ignore (dim)         — today's noise for you
//   • Move Drivers (amber)         — what's driving QQQ/SPY now
//
// "Generate today's read" button if no read exists for today.
// V2 will add a 7:30am ET cron that auto-generates per
// workstation.
// ═══════════════════════════════════════════════════════════

import { useEffect, useState, useCallback } from 'react';
import { ShiftRead } from '../shared';
import { useWidgetFooterAction } from '../../../components/widgets/widget-card';

const CONFIDENCE_TONE = {
  high:     'sage',
  moderate: 'amber',
  low:      'dim',
};

const DRIVER_KIND_TONES = {
  macro:     'gold',
  earnings:  'sage',
  sector:    'teal',
  sentiment: 'purple',
  dollar:    'steel',
  premarket: 'amber',
  news:      'terra',
};

export default function DailyReadSection({ workstation, userId }) {
  const [dailyRead, setDailyRead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  // ─── Load today's read ───────────────────────────────────
  const load = useCallback(async () => {
    if (!workstation?.id || !userId) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams({ workstation_id: workstation.id, user_id: userId });
      const res = await fetch(`/api/workstations/daily-read?${qs}`, { cache: 'no-store' });
      const data = res.ok ? await res.json() : { daily_read: null };
      setDailyRead(data.daily_read);
      return data.daily_read;
    } catch (e) {
      console.warn('[daily-read] load failed:', e?.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [workstation?.id, userId]);

  useEffect(() => { load(); }, [load]);

  // ─── Generate ────────────────────────────────────────────
  const handleGenerate = useCallback(async (forceRegen = false) => {
    if (!workstation?.id || !userId || generating) return;
    setGenerating(true);
    setError(null);
    try {
      const res = await fetch('/api/workstations/daily-read?action=generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          workstation_id: workstation.id,
          force_regenerate: forceRegen,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data?.error || `HTTP ${res.status}`);
        return;
      }
      setDailyRead(data.daily_read);
    } catch (e) {
      setError(e?.message || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  }, [workstation?.id, userId, generating]);

  // ─── Auto-generate on open (or when stale) ─────────────
  // Fires if either:
  //   (a) no read loaded yet, OR
  //   (b) the loaded read’s read_date is NOT today’s ET date
  //       — i.e. we’re showing a stale row from a prior day
  //       and should regenerate (force=true).
  // Gated by localStorage so we don’t spam the generator more
  // than once per workstation/user/day. Tokens matter.
  useEffect(() => {
    if (loading || generating) return;
    if (!workstation?.id || !userId) return;

    const todayKey = etDateKey();
    const hasRead = !!dailyRead;
    const isStale = hasRead && dailyRead.read_date && dailyRead.read_date !== todayKey;

    // If a fresh (today) read is already loaded, nothing to do.
    if (hasRead && !isStale) return;

    const lsKey = `ws:daily-read:autogen:${userId}:${workstation.id}:${todayKey}`;
    try {
      if (typeof window !== 'undefined' && window.localStorage.getItem(lsKey) === '1') {
        return; // already tried today
      }
    } catch { /* SSR or storage blocked — fall through */ }

    // Only auto-kick within plausible trading hours (6am-8pm ET).
    const hourET = etHour();
    if (hourET == null || hourET < 6 || hourET > 20) return;

    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(lsKey, '1');
      }
    } catch { /* best-effort */ }

    // If stale, force regen so the API doesn’t dedup to the old row.
    handleGenerate(isStale).catch(() => {});
  }, [loading, dailyRead, workstation?.id, userId, generating, handleGenerate]);

  // ─── Render ──────────────────────────────────────────────
  return (
    <section className="ws-section ws-section-daily-read">
      <div className="ws-section-header">
        <div className="ws-section-label">Today&rsquo;s Read</div>
        <div className="ws-section-tag">
          {dailyRead?.read_date && (
            <span>{dailyRead.read_date} · {dailyRead.source_snapshot_window}</span>
          )}
        </div>
      </div>

      <div className="ws-section-body">
        {loading && <div className="ws-section-empty-text">Loading…</div>}

        {!loading && !dailyRead && (
          <div className="ws-daily-read-empty">
            <div className="ws-daily-read-empty-title">No Daily Read for today yet</div>
            <div className="ws-daily-read-empty-body">
              Shift will read today&rsquo;s 7-force snapshot, connect it to YOUR
              criteria, and produce a personalized brief: market direction,
              your direction, the gap, watch list, what to ignore, and the
              move drivers behind QQQ/SPY today.
            </div>
            <button
              type="button"
              className="ws-daily-read-generate-btn"
              onClick={() => handleGenerate(false)}
              disabled={generating}
            >
              {generating ? 'Generating…' : '+ Generate today’s read'}
            </button>
            {error && <div className="ws-daily-read-error">{error}</div>}
          </div>
        )}

        {!loading && dailyRead && (
          <DailyReadBody
            read={dailyRead}
            onRegenerate={() => handleGenerate(true)}
            generating={generating}
            error={error}
          />
        )}
      </div>
    </section>
  );
}

// ─── Body when a read exists ───────────────────────────────
function DailyReadBody({ read, onRegenerate, generating, error }) {
  const market   = read.market_direction || {};
  const personal = read.personal_direction || {};
  const gap      = read.gap || {};
  const watch    = Array.isArray(read.watch_list) ? read.watch_list : [];
  const ignore   = Array.isArray(read.what_to_ignore) ? read.what_to_ignore : [];
  const drivers  = Array.isArray(read.move_drivers) ? read.move_drivers : [];
  const activeSignals = Array.isArray(read.personal_signals_active) ? read.personal_signals_active : [];

  // Detect whether the loaded read is from a prior day. If so,
  // everything below ('today's primary catalyst', etc.) is stale
  // — the auto-regen effect in the outer component will kick
  // in, but we surface a visible flag so the user knows while
  // the regen runs.
  const todayKey = etDateKey();
  const isStale = read.read_date && read.read_date !== todayKey;

  return (
    <div className="ws-daily-read-body">
      <div className="ws-daily-read-headline">
        <div className="ws-daily-read-title">{read.title}</div>
        {read.summary && <div className="ws-daily-read-summary">{read.summary}</div>}
        {isStale && (
          <div className="ws-daily-read-stale" title={`This read was generated on ${read.read_date}. Regenerating with today’s snapshot.`}>
            <span className="ws-daily-read-stale-dot" aria-hidden />
            Stale — from {read.read_date}. {generating ? 'Regenerating…' : 'Tap Regenerate to refresh.'}
          </div>
        )}
        {/* Commit 69 — Regenerate button moved to footer of widget
            (see end of this component). Removed from headline so the
            title + summary aren't competing visually with the action. */}
      </div>

      {error && <div className="ws-daily-read-error">{error}</div>}

      {/* Personal Signals (FT-4) — the moat made visible */}
      {activeSignals.length > 0 && (
        <PersonalSignalsCallout signals={activeSignals} />
      )}

      {/* Move Drivers — what's actually moving QQQ/SPY today */}
      {drivers.length > 0 && (
        <div className="ws-daily-read-drivers">
          <div className="ws-daily-read-eyebrow">Move Drivers · Today</div>
          <div className="ws-daily-read-drivers-list">
            {drivers.map((d, i) => (
              <div key={i} className={`ws-daily-read-driver ws-daily-read-driver-${d.impact || 'low'}`}>
                <div className="ws-daily-read-driver-row">
                  <span className={`ws-pill ws-pill-${DRIVER_KIND_TONES[d.kind] || 'dim'}`}>{d.kind || 'driver'}</span>
                  <span className={`ws-pill ws-pill-${impactTone(d.impact)}`}>{d.impact || 'low'}</span>
                </div>
                <div className="ws-daily-read-driver-name">{d.driver}</div>
                {d.notes && <div className="ws-daily-read-driver-notes">{d.notes}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Market Direction (steel) */}
      <DirectionCard
        tone="steel"
        eyebrow="Market Direction"
        sub="What everyone sees · the consensus institutional read"
        d={market}
      />

      {/* Your Direction (gold) */}
      <DirectionCard
        tone="gold"
        eyebrow="Your Direction"
        sub="What your criteria do with this · your edge within the move"
        d={personal}
      />

      {/* The Gap (purple) */}
      {gap.body && (
        <div className="ws-daily-read-gap ws-top-accent-purple">
          <div className="ws-daily-read-eyebrow ws-tone-purple">The Gap Between Them</div>
          <div className="ws-daily-read-eyebrow-sub">Where your edge lives today</div>
          <div className="ws-daily-read-gap-body">{gap.body}</div>
          {gap.opportunity_type && (
            <span className={`ws-pill ws-pill-${opportunityTone(gap.opportunity_type)}`}>
              {opportunityLabel(gap.opportunity_type)}
            </span>
          )}
        </div>
      )}

      {/* Watch List (teal) */}
      {watch.length > 0 && (
        <div className="ws-daily-read-watch ws-top-accent-teal">
          <div className="ws-daily-read-eyebrow ws-tone-teal">Watch List</div>
          <div className="ws-daily-read-eyebrow-sub">Conditions to watch in QQQ/SPY today · your criteria</div>
          <ul className="ws-daily-read-watch-list">
            {watch.map((w, i) => (
              <li key={i} className="ws-daily-read-watch-item">
                <div className="ws-daily-read-watch-cond">{w.condition}</div>
                <div className="ws-daily-read-watch-meta">
                  {w.timeframe && <span className="ws-daily-read-watch-tf">{w.timeframe}</span>}
                  {w.why && <span className="ws-daily-read-watch-why"> — {w.why}</span>}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* What to Ignore */}
      {ignore.length > 0 && (
        <div className="ws-daily-read-ignore">
          <div className="ws-daily-read-eyebrow ws-tone-dim">What to Ignore</div>
          <div className="ws-daily-read-eyebrow-sub">Today&rsquo;s noise for your criteria · skip these</div>
          <ul className="ws-daily-read-ignore-list">
            {ignore.map((it, i) => (
              <li key={i} className="ws-daily-read-ignore-item">
                <div className="ws-daily-read-ignore-section">{it.section}</div>
                {it.why && <div className="ws-daily-read-ignore-why">{it.why}</div>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <ShiftRead variant="purple">
        These are conditions worth having on your radar — not recommendations.
        The market told everyone the same thing this morning. This Daily Read
        tells you what today&rsquo;s forces mean for <strong>your</strong>{' '}
        criteria, so you can act from your edge inside the broader move.
      </ShiftRead>

      {/* Commit 72 — body-internal footer removed; the freshness
          line stays here, but the Regenerate button is injected
          into the WidgetCard chrome footer-right via context (see
          DailyReadFooterAction below). */}
      {read.read_date && (
        <div className="ws-daily-read-freshness">
          read · <strong>{read.read_date}</strong>
          {read.generated_at && (
            <> · {new Date(read.generated_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })} ET</>
          )}
        </div>
      )}

      <DailyReadFooterAction onRegenerate={onRegenerate} generating={generating} />
    </div>
  );
}

// ─── Commit 72 — injects the Regenerate button into the
// WidgetCard chrome footer-right via WidgetFooterContext. The
// component renders nothing itself; it just calls setFooterAction.
function DailyReadFooterAction({ onRegenerate, generating }) {
  const setFooterAction = useWidgetFooterAction();
  useEffect(() => {
    setFooterAction(
      <button
        type="button"
        className="ws-card-footer-action-btn"
        onClick={onRegenerate}
        disabled={generating}
        title="Regenerate using the latest force snapshot"
      >
        {generating ? 'Regenerating…' : '↻ Regenerate'}
      </button>
    );
    return () => setFooterAction(null);
  }, [setFooterAction, onRegenerate, generating]);
  return null;
}

// ─── Personal Signals Callout (FT-4) ──────────────────────
//
// The visible moat. When the Daily Read brief had personal
// pattern_signals active in today's snapshot, surface them
// as a high-prominence callout above the Market/Personal
// direction cards. Each card shows the math.
function PersonalSignalsCallout({ signals }) {
  if (!signals || signals.length === 0) return null;
  return (
    <div className="ws-daily-read-signals ws-top-accent-gold">
      <div className="ws-daily-read-eyebrow ws-tone-gold">Your Personal Signals · Active Today</div>
      <div className="ws-daily-read-eyebrow-sub">
        From your Signal Matrix · {signals.length} signal{signals.length === 1 ? '' : 's'} match
        today&rsquo;s conditions
      </div>
      <div className="ws-daily-read-signals-grid">
        {signals.map((s) => {
          const baseline = s.win_rate_baseline;
          const condition = s.win_rate_condition;
          const effect = s.effect_size_pp;
          const dir = effect > 0 ? 'sage' : effect < 0 ? 'terra' : 'dim';
          const arrow = effect > 0 ? '↑' : effect < 0 ? '↓' : '→';
          return (
            <div key={s.id} className={`ws-daily-read-signal-card ws-top-accent-${dir}`}>
              <div className="ws-daily-read-signal-row">
                <span className={`ws-pill ws-pill-${
                  s.confidence_label === 'high' ? 'gold'
                  : s.confidence_label === 'confirmed' ? 'sage'
                  : 'amber'
                }`}>
                  {s.confidence_label}
                </span>
                {s.status === 'monitoring' && (
                  <span className="ws-pill ws-pill-dim">monitoring</span>
                )}
              </div>
              <div className="ws-daily-read-signal-label">{s.condition_label}</div>
              <div className="ws-daily-read-signal-math">
                <span className="ws-daily-read-signal-baseline">{Math.round(baseline)}%</span>
                <span className={`ws-daily-read-signal-arrow ws-tone-${dir}`}>{arrow}</span>
                <span className={`ws-daily-read-signal-condition ws-tone-${dir}`}>{Math.round(condition)}%</span>
                <span className={`ws-daily-read-signal-delta ws-pill ws-pill-${dir}`}>
                  {effect > 0 ? '+' : ''}{Math.round(effect)}pp
                </span>
              </div>
              <div className="ws-daily-read-signal-meta">
                {s.sessions_matching} of {s.sessions_total} sessions match
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DirectionCard({ tone, eyebrow, sub, d }) {
  if (!d.headline && !d.body) return null;
  const confTone = CONFIDENCE_TONE[(d.confidence || '').toLowerCase()] || 'dim';
  return (
    <div className={`ws-daily-read-direction ws-top-accent-${tone}`}>
      <div className="ws-daily-read-direction-header">
        <div>
          <div className={`ws-daily-read-eyebrow ws-tone-${tone}`}>{eyebrow}</div>
          <div className="ws-daily-read-eyebrow-sub">{sub}</div>
        </div>
        {d.confidence && (
          <span className={`ws-pill ws-pill-${confTone}`}>{d.confidence} confidence</span>
        )}
      </div>
      {d.headline && <div className="ws-daily-read-headline-text">{d.headline}</div>}
      {d.body && <div className="ws-daily-read-body-text">{d.body}</div>}
      {Array.isArray(d.applies_to) && d.applies_to.length > 0 && (
        <div className="ws-daily-read-applies">
          <span className="ws-daily-read-applies-label">Applies to:</span>
          {d.applies_to.map((s, i) => (
            <span key={i} className="ws-pill ws-pill-gold">{s}</span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────
function impactTone(impact) {
  if (impact === 'high') return 'gold';
  if (impact === 'medium') return 'amber';
  return 'dim';
}
function opportunityTone(kind) {
  if (kind === 'alignment') return 'sage';
  if (kind === 'divergence') return 'purple';
  return 'dim';
}
function opportunityLabel(kind) {
  if (kind === 'alignment') return 'Aligned with market — same direction at scale';
  if (kind === 'divergence') return 'Divergent from market — your edge is the gap';
  if (kind === 'noise') return 'Mostly noise for your criteria';
  return kind;
}

// TIGHT-3: ET helpers for auto-generate gating
function etDateKey() {
  try {
    return new Date().toLocaleDateString('en-CA', {
      timeZone: 'America/New_York',
      year: 'numeric', month: '2-digit', day: '2-digit',
    });
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}
function etHour() {
  try {
    const h = new Date().toLocaleString('en-US', {
      timeZone: 'America/New_York',
      hour: 'numeric',
      hour12: false,
    });
    const n = parseInt(h, 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}
